"use client";

/**
 * CandidateDetailDisplay - Single candidate detail display
 */

import { useState } from "react";
import { Avatar, Tag } from "@/ui";
import { colors, spacing, textStyles } from "@/ui/tokens";
import {
  Person,
  Email,
  Phone,
  LocationOn,
  Work,
  School,
  LocalOffer,
  PersonOff,
  Notes,
  Code,
} from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  ToolEmptyState,
  ToolDivider,
  ToolSectionHeader,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";
import { displayValue } from "../utils";

interface CandidateDetail {
  id: string | number;
  name?: string;
  email?: string;
  phone?: string;
  title?: string;
  location?: string;
  avatar?: string;
  summary?: string;
  skills?: Array<string | { id?: number; name?: string; [key: string]: unknown }>;
  tags?: Array<string | { id?: number; name?: string; [key: string]: unknown }>;
  availability?: string;
  experience?: Array<{
    company: string | { id?: number; name?: string; [key: string]: unknown };
    position: string;
    period?: string;
    description?: string;
  }>;
  education?: Array<{
    institution: string | { id?: number; name?: string; [key: string]: unknown };
    degree: string;
    year?: string;
  }>;
}

interface DetailOutput {
  success?: boolean;
  candidate?: CandidateDetail;
  error?: string;
}

export function CandidateDetailDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const toolState = deriveToolState(toolCall);
  const { status, isLoading, isError, error } = toolState;

  const result = toolCall.finalOutput as DetailOutput | undefined;
  const candidate = result?.candidate;

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title={candidate?.name || "Candidate Details"}
        toolName={toolCall.toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<Person style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded} noPadding>
        {isLoading && (
          <div style={{ padding: spacing[5], textAlign: "center" }}>
            <ToolProgress variant="dots" label="Loading candidate..." />
          </div>
        )}

        {isError && error && (
          <div style={{ padding: spacing[4] }}>
            <ToolError error={error} />
          </div>
        )}

        {!isLoading && candidate && (
          <>
            {/* Profile Header */}
            <div style={{ padding: spacing[5], display: "flex", gap: spacing[5], alignItems: "flex-start" }}>
              <Avatar
                src={candidate.avatar}
                name={candidate.name}
                size="xl"
              />
              <div style={{ flex: 1 }}>
                <span
                  style={{
                    ...textStyles.subtitle1,
                    fontWeight: 600,
                    display: "block",
                  }}
                >
                  {candidate.name}
                </span>
                {candidate.title && (
                  <span style={{ ...textStyles.body1, color: colors.text.muted, display: "block" }}>
                    {candidate.title}
                  </span>
                )}
                <div style={{ display: "flex", gap: spacing[4], marginTop: spacing[2], flexWrap: "wrap" }}>
                  {candidate.email && (
                    <div style={{ display: "flex", alignItems: "center", gap: spacing[1] }}>
                      <Email style={{ fontSize: 16, color: colors.text.muted }} />
                      <span style={textStyles.body2}>{candidate.email}</span>
                    </div>
                  )}
                  {candidate.phone && (
                    <div style={{ display: "flex", alignItems: "center", gap: spacing[1] }}>
                      <Phone style={{ fontSize: 16, color: colors.text.muted }} />
                      <span style={textStyles.body2}>{candidate.phone}</span>
                    </div>
                  )}
                  {candidate.location && (
                    <div style={{ display: "flex", alignItems: "center", gap: spacing[1] }}>
                      <LocationOn style={{ fontSize: 16, color: colors.text.muted }} />
                      <span style={textStyles.body2}>{candidate.location}</span>
                    </div>
                  )}
                </div>
                {candidate.availability && (
                  <Tag
                    variant="signal"
                    size="sm"
                    color={candidate.availability.toLowerCase().includes("available") ? "success" : "neutral"}
                    style={{ marginTop: spacing[2] }}
                  >
                    {candidate.availability}
                  </Tag>
                )}
              </div>
            </div>

            {/* Summary */}
            {candidate.summary && (
              <>
                <ToolDivider />
                <div style={{ paddingLeft: spacing[5], paddingRight: spacing[5], paddingTop: spacing[4], paddingBottom: spacing[4] }}>
                  <ToolSectionHeader icon={<Notes />} label="Summary" compact />
                  <span style={{ ...textStyles.body2, display: "block" }}>{candidate.summary}</span>
                </div>
              </>
            )}

            {/* Skills */}
            {candidate.skills && candidate.skills.length > 0 && (
              <>
                <ToolDivider />
                <div style={{ paddingLeft: spacing[5], paddingRight: spacing[5], paddingTop: spacing[4], paddingBottom: spacing[4] }}>
                  <ToolSectionHeader icon={<Code />} label="Skills" />
                  <div style={{ display: "flex", gap: spacing[1], flexWrap: "wrap" }}>
                    {candidate.skills.map((skill, i) => (
                      <Tag key={i} variant="default" size="sm">
                        {displayValue(skill)}
                      </Tag>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Experience */}
            {candidate.experience && candidate.experience.length > 0 && (
              <>
                <ToolDivider />
                <div style={{ paddingLeft: spacing[5], paddingRight: spacing[5], paddingTop: spacing[4], paddingBottom: spacing[4] }}>
                  <ToolSectionHeader icon={<Work />} label="Experience" />
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {candidate.experience.map((exp, i) => (
                      <div key={i} style={{ paddingTop: spacing[1], paddingBottom: spacing[1] }}>
                        <span style={{ ...textStyles.body2, fontWeight: 500, display: "block" }}>
                          {exp.position} at {displayValue(exp.company)}
                        </span>
                        {(exp.period || exp.description) && (
                          <span style={{ ...textStyles.caption, color: colors.text.muted, display: "block" }}>
                            {exp.period || exp.description}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Education */}
            {candidate.education && candidate.education.length > 0 && (
              <>
                <ToolDivider />
                <div style={{ paddingLeft: spacing[5], paddingRight: spacing[5], paddingTop: spacing[4], paddingBottom: spacing[4] }}>
                  <ToolSectionHeader icon={<School />} label="Education" />
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {candidate.education.map((edu, i) => (
                      <div key={i} style={{ paddingTop: spacing[1], paddingBottom: spacing[1] }}>
                        <span style={{ ...textStyles.body2, fontWeight: 500, display: "block" }}>
                          {edu.degree}
                        </span>
                        <span style={{ ...textStyles.caption, color: colors.text.muted, display: "block" }}>
                          {displayValue(edu.institution)}{edu.year ? ` (${edu.year})` : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Tags */}
            {candidate.tags && candidate.tags.length > 0 && (
              <>
                <ToolDivider />
                <div style={{ paddingLeft: spacing[5], paddingRight: spacing[5], paddingTop: spacing[4], paddingBottom: spacing[4] }}>
                  <ToolSectionHeader icon={<LocalOffer />} label="Tags" muted />
                  <div style={{ display: "flex", gap: spacing[1], flexWrap: "wrap" }}>
                    {candidate.tags.map((tag, i) => (
                      <Tag key={i} variant="minimalOutline" size="sm">
                        {displayValue(tag)}
                      </Tag>
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {!isLoading && !candidate && !isError && (
          <ToolEmptyState icon={<PersonOff />} message="Candidate not found" />
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default CandidateDetailDisplay;
